import { DefaultButton, Icon, MessageBar, MessageBarType, Pivot, PivotItem, PrimaryButton, Spinner, SpinnerSize, TooltipHost } from "@fluentui/react";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { PermissionRole, PowerAppProvisioningStatus, PowerAppSolutionChangeTaskType, PowerAppsScope, PowerAppStatus } from "../../GlobalConst/globalEnums";
import Api from '../../services/Api';
import LoadingIcon from "../formFields/LoadingIcon";
import SolutionCompliance from "./PowerAppEditElements/SolutionCompliance";
import SolutionComponents from "./PowerAppEditElements/SolutionComponents";
import SolutionDetails from "./PowerAppEditElements/SolutionDetails";
import PowerAppSolutionTasks from "./PowerAppSolutionTasks";
import LightWeightAssessment from "./PowerAppEditElements/LightWeightAssessment";

const CancelToken = axios.CancelToken;

const PowerAppSolutionEditor = ({ solutionId, callBack, onRequestClose, isEditable, onEditChange }) => {
    
    const tabs = {
        details: 'details',
        components: 'components',
        compliance: 'compliance',
        tasks: 'tasks',
        riskProfile: 'riskProfile'
    }
    const cancelSource = useRef(null);
    const currentUser = useSelector((state) => state.user.currentUser)

    const [solution, setSolution] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [editable, setEditable] = useState(isEditable);
    const [updateSet, setUpdateSet] = useState([]);
    const [error, setError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [messageVisible, setMessageVisible] = useState(false);
    const [showTabSwitchDialog, setShowTabSwitchDialog] = useState(false)
    const [selectedTab, setSelectedTab] = useState(tabs.details)
    const [newSelectedTab, setNewSelectedTab] = useState()
    const [recertificationTask, setRecertificationTask] = useState()
    const [isRefreshed, setIsRefreshed] = useState({})
    const [showDeletionRequestSavedDialog, setShowDeletionRequestSavedDialog] = useState(false)

    const getRecertificationTask = (tasks) =>{
        return tasks.find(x => x.type === PowerAppSolutionChangeTaskType.recertification)
    }

    // const loadSolutionWithoutOwners = (solution) => {
    //     let newSolution = { ...solution };
        
    //     newSolution.primaryOwner = getValue('primaryOwner', true, solution)
    //     newSolution.secondaryOwner = getValue('secondaryOwner', true, solution)

    //     setSolution(newSolution)
    //     setUpdateSet([])
    // }

    const getPowerAppSolution = useCallback((id, isDeleteRequest = false) => {
        setIsLoading(true);

        Api.get(`/PowerAppSolutionsPanel?id=${id}`, { cancelToken: cancelSource.current.token })
            .then(res => res.data)
            .then(solution => {
                setSolution(solution)
                setUpdateSet([])
                setIsLoading(false);
                setRecertificationTask(getRecertificationTask(solution.changeTasks))

                if (isDeleteRequest)
                    setShowDeletionRequestSavedDialog(true)

                if (callBack) 
                    callBack(solution);
            });
    }, []);

    useEffect(() => {
        cancelSource.current = CancelToken.source();

        getPowerAppSolution(solutionId);

        return () => {
            cancelSource.current.cancel();
        }
    }, [solutionId, getPowerAppSolution]);

    useEffect(() => {
      onEditChange(editable)
    }, [editable])
    
    const refresh = (updateIsRefreshed = true, isDeleteRequest = false) => {
        getPowerAppSolution(solutionId, isDeleteRequest);
        if (updateIsRefreshed)
            setIsRefreshed({...isRefreshed, [selectedTab]: true})
    };

    const updateChange = (key, value, callBack) => {
        setUpdateSet((prevSet) => {
            let set = prevSet.filter(x => x.key !== key);
            if (JSON.stringify(solution[key]) === JSON.stringify(value) || (solution[key] === null && value === '')) {
                if (callBack) callBack();
                return set
            }
            set.push({ key, value });
            if (callBack) callBack();
            return set
        })
    };

    const updatePersonChange = (key, value, isLimited, callBack) => {
        setUpdateSet((prevSet) => {
            let set = prevSet.filter(x => x.key !== key);
            if (isLimited)
                value = value.length ? value[0] : null

            if ((solution[key] || {}).userId === (value || {}).id) {
                if (callBack) callBack();
                return set
            }
            set.push({ key, value: isLimited ? {value} : value });
            if (callBack) callBack();
            return set
        })
    }

    const updateDimensionChange = (key, keyId, value, idField = 'id', isNullableObject = false, callBack) => {
        setUpdateSet((prevSet) => {
            const isMultiValue = Array.isArray(value)

            let set = prevSet.filter(x => x.key !== key && x.key !== keyId);
            const parsedValue = isMultiValue
                                ? value.map(x => x[idField]) 
                                : isNullableObject
                                    ? (value ? {value: value[idField]} : {value: null})
                                    : (value ? value[idField] : 0)
            const parsedDefaultValue = isMultiValue 
                                        ? solution[key].map(x => x[idField]) 
                                        : solution[key]
                                            ? solution[key][idField]
                                                ? solution[key][idField]
                                                : solution[key]
                                            : 0;
                            
            if (JSON.stringify(parsedDefaultValue) === JSON.stringify(parsedValue) || parsedDefaultValue.toString() === ((isNullableObject ? parsedValue.value : parsedValue) || 0).toString()) {
                if (callBack) callBack();
                    return set;
            }
            set.push({ key, value });
            set.push({ 
                key: keyId, 
                value: parsedValue
            });
            if (callBack) callBack();
            return set
        })
    }

    const handleEditable = () => {
        setEditable(true);
        setMessageVisible(false)
    };

    const eraseSelection = () => {
        //let update = buildUpdate();
        //if (update.extension) {
        //    update.expirationDate = new Date(new Date().setFullYear(new Date().getFullYear() + update.extension)).toISOString();
        //}
        //setSolution({ ...solution, ...update });
        setEditable(false);
        setError(false);
    };

    const buildUpdate = (recertify, restore, deleteSolution, restoreDeleted) => {
        let command = { id: solutionId };
        updateSet.forEach(x => command[x.key] = x.value);

        command['recertify'] = !!recertify
        command['restore'] = !!restore
        command['delete'] = !!deleteSolution
        command['restoreDeleted'] = !!restoreDeleted

        return command;
    };

    const updateSolution = (ev, recertify, restore, deleteSolution, restoreDeleted) => {
        eraseSelection()
        setMessageVisible(false)
        setIsLoading(true)
        setIsSaving(true)

        Api.post('UpdatePowerAppSolutionRequest', buildUpdate(recertify, restore, deleteSolution, restoreDeleted))
            .then(res => res.data)
            .then(post => {
                setIsLoading(false);
                setIsSaving(false);
                if (post === 200) {
                    setMessageVisible(true);
                    refresh(true, deleteSolution)
                } else {
                    setError(true);
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const saveEnabled = () => {
        return !isLoading
            && updateSet.length !== 0
            && !isSaving
            //&& isChildrenUpdateValid
            && !editEnabled()
            && !recertificationTask
            && editable
    };
    
    const isRecertification = () => {
        return !isLoading
            && !isSaving
            && !editEnabled()
            && recertificationTask
    };

    const editEnabled = () => {
        return solution
            && !isRefreshed[selectedTab]
            && !isSaving 
            && !editable 
            && solution.status !== PowerAppStatus.deleted
            && (currentUser.role === PermissionRole.administrator || currentUser.role === PermissionRole.contributor
                || ![PowerAppProvisioningStatus.updating, PowerAppProvisioningStatus.pendingApproval, PowerAppProvisioningStatus.partiallyFailed, PowerAppProvisioningStatus.failed, PowerAppProvisioningStatus.permanentFailed, PowerAppProvisioningStatus.expired]
                        .includes(solution.provisioningStatus)
                )
            && solution.provisioningStatus !== PowerAppProvisioningStatus.expired
    };
    
    const restoreEnabled = () => {
        return !isLoading
            && !isSaving
            && !recertificationTask
            && solution.provisioningStatus === PowerAppProvisioningStatus.expired
            && solution.isHighestSolution
            && !isRefreshed[selectedTab]
    }

    const onSaveDialog = () => {
        updateSolution()
        setShowTabSwitchDialog(false)
        setSelectedTab(newSelectedTab)
    }

    const onDiscardDialog = () => {
        setUpdateSet([])
        setEditable(false)
        setShowTabSwitchDialog(false)
        if (newSelectedTab !== tabs.compliance || solution.scope === PowerAppsScope.businessApplication)
            setSelectedTab(newSelectedTab)
    }

    const onTabChanged = (item, ev) => {
        setMessageVisible(false)
        setError()
        if (!isRefreshed[selectedTab] && updateSet.length > 0 && (selectedTab === tabs.details || selectedTab === tabs.compliance || selectedTab === tabs.riskProfile)) {
            ev.preventDefault()
            setNewSelectedTab(item.props.itemKey)
            setShowTabSwitchDialog(true)
        } else {
            setSelectedTab(item.props.itemKey)
        }
    }

    const getValue = (key, isNullableObject = false, defaultSet = null) => {
        const result = updateSet.find(x => x.key === key)
        if (result)
            return isNullableObject ? (result.value || {}).value : result.value
        else
            return  defaultSet ? defaultSet[key] : (solution || {})[key]
    }

    const btnIconEdit = solution && solution.isHighestSolution  && (selectedTab === tabs.details || selectedTab === tabs.compliance || selectedTab === tabs.riskProfile) &&
        <Icon style={{fontSize: 17}} iconName='edit' onClick={handleEditable} />

    const btnEdit = solution &&
        <TooltipHost content={!solution.isHighestSolution && 'There is a version of this solution available on higher environment (Dev -> Stage -> Prod). Please update it on the highest environment.'}>
            <DefaultButton className="edit-icon" onClick={handleEditable} disabled={!solution.isHighestSolution}>Edit</DefaultButton>
        </TooltipHost>

    const btnSave = (isEnabled) =>
        <PrimaryButton className="edit-icon" onClick={updateSolution} disabled={!isEnabled}>Save</PrimaryButton>

    const btnRecertify = (isChildrenUpdateValid) => recertificationTask &&
        <TooltipHost content={isChildrenUpdateValid ? `Recertification will be valid for ${recertificationTask.additionalData}.` : 'Please provide all required fields to complete the recertification process.'}>
            <PrimaryButton className="edit-icon" onClick={(ev) => updateSolution(ev, true)} disabled={!isChildrenUpdateValid}>Recertify</PrimaryButton>
        </TooltipHost>

    const messageBar =
        <div>
            {
                editable && selectedTab === tabs.details && solution && (currentUser.role === PermissionRole.administrator || solution.isOwnedViaServiceLeads || solution.isOwnedViaEnvironmentOwner) &&
                <MessageBar
                    dismissButtonAriaLabel="Close"
                    messageBarType={MessageBarType.warning}>
                    <b>Notice:</b> You are recognized as an administrator for this environment and therefore permitted to make<br /> changes that bypass standard form validations.
                </MessageBar>
            }
            {
                error &&
                <MessageBar
                    dismissButtonAriaLabel="Close"
                    truncated={true}
                    messageBarType={MessageBarType.error}>
                    We cannot proceed your changes.
                </MessageBar>
            }
            {
                messageVisible &&
                <MessageBar
                    dismissButtonAriaLabel="Close"
                    truncated={true}
                    messageBarType={MessageBarType.success}>
                    Your changes have been saved.
                </MessageBar>
            }
        </div>

    const solutionTitle = solution && 
        <div className="site-editor-header">
            <div className="site-editor-title" title={solution.name}>
                {solution.name.length > 53 ? `${solution.name.substr(0, 50)}...` : solution.name}
            </div>
            <div className="site-editor-icon" onClick={onRequestClose}><Icon iconName="chromeClose" /></div>
            {editEnabled() && <div className="site-editor-icon">{btnIconEdit}</div>}
            {messageBar}
        </div>;
    
    const messagePanel = 
        <div className="edit-panel">
            {
                isSaving && 
                <Spinner style={{ float: 'left', marginLeft: 20 }} size={SpinnerSize.medium} />
            }
        </div>    
        
    const getChange = (key) => updateSet.find(x => x.key === key)

    const solutionDetails = solution && 
        <div>
            <SolutionDetails 
                solution={solution} 
                disabled={!editable || isRefreshed[selectedTab]}
                onUpdate={updateChange}
                onPersonUpdate={updatePersonChange}
                onDimensionUpdate={updateDimensionChange}
                getValue={getValue}
                isSaveEnabled={saveEnabled}
                isEditEnabled={editEnabled}
                isRecertification={isRecertification}
                btnEdit={btnEdit}
                btnSave={btnSave}
                btnRecertify={btnRecertify}
                isRestoreEnabled={restoreEnabled}
                messagePanel={messagePanel}
                showTabSwitchDialog={showTabSwitchDialog}
                onDiscardDialog={onDiscardDialog}
                onSaveDialog={onSaveDialog}
                onCancelDialog={() => setShowTabSwitchDialog(false)}
                getChange={getChange}
                onSaveCallback={refresh}
                updateSolution={updateSolution}
                showDeletionRequestSavedDialog={showDeletionRequestSavedDialog}
                setShowDeletionRequestSavedDialog={setShowDeletionRequestSavedDialog}
                isSaving={isSaving}
                isRefreshed={isRefreshed[selectedTab]}
                isMainForm={true}
            />
        </div>;

    const solutionCompliance = solution &&
        <div>
            <SolutionCompliance
                solution={solution} 
                disabled={!editable || isRefreshed[selectedTab]}
                onUpdate={updateChange}
                isSaveEnabled={saveEnabled}
                isEditEnabled={editEnabled}
                btnEdit={btnEdit}
                btnSave={btnSave}
                messagePanel={messagePanel}
                showTabSwitchDialog={showTabSwitchDialog}
                onDiscardDialog={onDiscardDialog}
                onSaveDialog={onSaveDialog}
                onCancelDialog={() => setShowTabSwitchDialog(false)}
            />
        </div>;
        
    const lightWeightAssessment = solution &&
        <div>
            <LightWeightAssessment
                solution={solution} 
                disabled={!editable || isRefreshed[selectedTab]}
                onUpdate={updateChange}
                isSaveEnabled={saveEnabled}
                isEditEnabled={editEnabled}
                btnEdit={btnEdit}
                btnSave={btnSave}
                messagePanel={messagePanel}
                showTabSwitchDialog={showTabSwitchDialog}
                onDiscardDialog={onDiscardDialog}
                onSaveDialog={onSaveDialog}
                onCancelDialog={() => setShowTabSwitchDialog(false)}
                getValue={getValue}
            />
        </div>;

    return (
        isLoading || !solution
        ? <LoadingIcon />
        :
        <div style={{height: '100%'}}>
            {solutionTitle}
            <div className='site-editor-container'>
                <Pivot onLinkClick={onTabChanged} selectedKey={selectedTab}>
                    <PivotItem itemKey={tabs.details} headerText="Solution details">
                        {solutionDetails}
                    </PivotItem>
                    <PivotItem itemKey={tabs.components} headerText="Solution components">
                    {
                        solution.groupedSolutions.map((x, index) => <SolutionComponents solution={x} key={index} showByDefault={index === 0} />)
                    }
                    </PivotItem>
                    {
                        getValue('scope') === PowerAppsScope.businessApplication &&
                        <PivotItem itemKey={tabs.compliance} headerText="Compliance details">
                            {solutionCompliance}
                        </PivotItem>
                    }
                    {
                        <PivotItem itemKey={tabs.riskProfile} headerText="Risk Profile and Compliance">
                            {lightWeightAssessment}
                        </PivotItem>
                    }
                    {
                        solution.changeTasks.length && (currentUser.role === PermissionRole.administrator || solution.isOwnedViaServiceLeads || solution.isOwnedViaEnvironmentOwner) &&
                        <PivotItem itemKey={tabs.tasks} headerText="Tasks">
                            <PowerAppSolutionTasks app={solution} refresh={() => refresh(true)} />
                        </PivotItem>
                    }
                </Pivot>
            </div>
        </div>
    );
}

export default PowerAppSolutionEditor


// WEBPACK FOOTER //
// ./src/components/EditSite/PowerAppSolutionEditor.js